<template>
  <div v-if="isOpen" class="tw-fixed tw-inset-0 tw-z-[60] tw-flex tw-flex-col tw-bg-white tw-text-slate-900">
    <header
      class="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-bg-white tw-border-b tw-border-slate-200">
      <div class="tw-flex tw-items-center tw-gap-4">
        <div class="tw-bg-blue-50 tw-p-2.5 tw-rounded-xl">
          <font-awesome-icon icon="file-signature" class="tw-text-blue-600 tw-text-lg" />
        </div>
        <div>
          <h2 class="tw-text-lg tw-font-bold tw-text-slate-900">Document Studio</h2>
          <p class="tw-text-[10px] tw-text-slate-500 tw-uppercase tw-font-bold tw-tracking-wider tw-mt-0.5">Template
            Configuration</p>
        </div>
      </div>

      <div class="tw-flex tw-items-center tw-gap-2">
        <div v-if="pdfWidth && pdfHeight"
          class="tw-h-9 tw-px-3 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded-xl tw-text-[10px] tw-font-black tw-text-blue-600 tw-flex tw-items-center tw-gap-2">
          <span>{{ pdfWidth }}×{{ pdfHeight }}</span>
        </div>



        <Button @click="showGrid = !showGrid" :class="[
          'tw-h-9 tw-px-4 tw-rounded-xl tw-text-[10px] tw-font-black tw-uppercase tw-tracking-wider tw-transition-all tw-flex tw-items-center tw-gap-2',
          showGrid ? 'tw-bg-slate-900 tw-text-white' : 'tw-bg-slate-200 tw-text-slate-500'
        ]">
          <font-awesome-icon icon="border-all" :class="showGrid ? 'tw-text-blue-400' : ''" />
          Grid
        </Button>

        <Button @click="snapToGrid = !snapToGrid" :class="[
          'tw-h-9 tw-px-4 tw-rounded-xl tw-text-[10px] tw-font-black tw-uppercase tw-tracking-wider tw-transition-all tw-flex tw-items-center tw-gap-2',
          snapToGrid ? 'tw-bg-slate-900 tw-text-white' : 'tw-bg-slate-200 tw-text-slate-500'
        ]">
          <font-awesome-icon :icon="snapToGrid ? 'magnet' : 'link-slash'"
            :class="snapToGrid ? 'tw-text-emerald-400' : ''" />
          Snap: {{ snapToGrid ? 'ON' : 'OFF' }}
        </Button>

        <div class="tw-relative">
          <Button @click="showPreviewDropdown = !showPreviewDropdown" :disabled="!currentTemplate?.id || saving"
            class="tw-h-9 tw-px-5 tw-bg-slate-900 tw-text-white tw-rounded-xl tw-text-[10px] tw-font-black tw-uppercase tw-tracking-wider hover:tw-bg-slate-800 disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-2">
            <font-awesome-icon icon="eye" />
            Preview 
          </Button>
          <div v-if="showPreviewDropdown"
            class="tw-absolute tw-top-full tw-mt-2 tw-right-0 tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-shadow-lg tw-p-4 tw-z-50 tw-w-72">
            <button @click="showPreviewDropdown = false" class="tw-absolute tw-top-2 tw-right-2 tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-bg-grey-200 tw-rounded-lg hover:tw-bg-grey-800 tw-transition-colors">
              X
            </button>
            <label class="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-1 tw-block">Order ID</label>
            <div class="tw-flex tw-items-center tw-gap-2 tw-mb-3">
              <input v-model="sampleOrderId" type="number" placeholder="Enter Order ID"
                class="tw-flex-1 tw-h-9 tw-px-3 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-lg tw-text-xs tw-font-semibold tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
              <button @click="fetchSampleOrder" :disabled="!sampleOrderId"
                class="tw-h-9 tw-w-9 tw-flex tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-slate-200 disabled:tw-opacity-50 tw-shrink-0 tw-icon tw-icon-sm">
                <font-awesome-icon icon="magnifying-glass" />
              </button>
            </div>
            <div v-if="sampleOrder?.containers?.length" class="tw-mb-3">
              <label class="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-1 tw-block">Container</label>
              <div class="tw-flex tw-items-center tw-gap-2">
                <select v-model="sampleContainerId"
                  class="tw-flex-1 tw-h-9 tw-px-3 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-lg tw-text-xs tw-font-semibold tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                  <option v-for="(c, i) in sampleOrder.containers.toSorted((a,b) => a.internalIndex <= b.internalIndex ? -1 : 1)" :key="c.id" :value="c.id">
                    {{ String(c.internalIndex).padStart(2, '0') }} - {{ c.containerId }}
                  </option>
                </select>
                <button @click="fetchSampleContainer" :disabled="!sampleContainerId"
                  class="tw-h-9 tw-w-9 tw-flex tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-slate-200 disabled:tw-opacity-50 tw-shrink-0">
                  <font-awesome-icon icon="magnifying-glass" />
                </button>
              </div>
            </div>
            <div v-if="sampleContainer?.trips?.length" class="tw-mb-3">
              <label class="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-1 tw-block">Trip</label>
              <select v-model="sampleTripId"
                class="tw-w-full tw-h-9 tw-px-3 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-lg tw-text-xs tw-font-semibold tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                <option v-for="t in sortedTrips" :key="t.id" :value="t.id">
                  Leg {{ t.legId }} - {{ t.tripType }} (#{{ t.id }})
                </option>
              </select>
            </div>
            <Button @click="handlePreview" :disabled="!sampleOrder || !sampleContainer || !sampleTripId"
              class="tw-w-full tw-h-9 tw-bg-slate-900 tw-text-white tw-rounded-xl tw-text-[10px] tw-font-black tw-uppercase tw-tracking-wider hover:tw-bg-slate-800 disabled:tw-opacity-50 tw-flex tw-items-center tw-justify-center tw-gap-2">
              <font-awesome-icon icon="eye" />
              Preview
            </Button>
          </div>
        </div>

        <Button @click="handleDownloadTemplate()"
          :disabled="!currentTemplate?.id || saving"
          class="tw-h-9 tw-px-5 tw-bg-slate-900 tw-text-white tw-rounded-xl tw-text-[10px] tw-font-black tw-uppercase tw-tracking-wider hover:tw-bg-slate-800 disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-2">
          <font-awesome-icon icon="download" />
          Get PDF
        </Button>


        <Button @click="saveDefault()" :disabled="!currentTemplate || currentTemplate.isDefault" :class="[
          'tw-h-9 tw-px-3 tw-rounded-xl tw-text-[10px] tw-font-black tw-uppercase tw-tracking-wider tw-transition-all tw-flex tw-items-center tw-gap-2',
          currentTemplate?.isDefault
            ? 'tw-bg-blue-600/20 tw-text-blue-600 tw-border tw-border-blue-200'
            : 'tw-bg-slate-900 tw-text-white hover:tw-bg-slate-800'
        ]">
          {{ currentTemplate?.isDefault ? 'Current Default' : 'Make Default' }}
        </Button>

        <Button @click="saveFields" :loading="saving" :disabled="!pdfPreviewUrl"
          class="tw-h-9 tw-px-5 tw-bg-blue-600 tw-text-white tw-rounded-xl tw-text-[10px] tw-font-black tw-uppercase tw-tracking-wider hover:tw-bg-blue-700 disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-2">
          Save
        </Button>

        <button @click="$emit('close')"
          class="tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-text-slate-400 hover:tw-text-red-500 tw-transition-colors">
          <font-awesome-icon icon="xmark" class="tw-text-lg" />
        </button>
      </div>
    </header>

    <main class="tw-flex tw-flex-1 tw-min-h-0">
      <aside
        style="width: 420px;"
        class="tw-w-85 tw-bg-slate-50 tw-border-r tw-border-slate-200 tw-flex tw-flex-col tw-min-h-0 tw-overflow-y-auto custom-scrollbar">

        <div class="tw-p-6 tw-border-b tw-border-slate-200 tw-flex-none tw-text-left">
          <h2 class="tw-text tw-font-bold  tw-uppercase tw-tracking-widest tw-mb-5 ">
            Current template: <strong>{{ currentTemplate?.name || 'None' }}</strong>
          </h2>

          <button @click="toggleShowCompanyTemplateChooser()" class="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-bg-blue-600 tw-text-white tw-rounded-lg hover:tw-bg-blue-700 tw-transition-colors">
            {{ isShowCompanyTemplateChooser ? 'Hide Templates' : (!currentTemplate ? 'Choose Template':'Change Template') }}
          </button>

          <button @click="toggleShowTemplateUploader()" class="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-bg-blue-600 tw-text-white tw-rounded-lg hover:tw-bg-blue-700 tw-transition-colors">
            {{ isShowTemplateUploader ? 'Hide Add New' : 'Add New' }}
          </button>

          <button 
            v-if="currentTemplate && templates.length > 0"
            @click="toggleShowTemplateFieldsCopy()" 
            class="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-bg-blue-600 tw-text-white tw-rounded-lg hover:tw-bg-blue-700 tw-transition-colors">
            {{ isShowTemplateFieldsCopy ? 'Hide Copy' : 'Copy from Template' }}
          </button>

          <div class="tw-p-6 tw-flex-none template-reveal-animation tw-shadow-lg tw-rounded-lg tw-w-full tw-relative" v-if="isShowCompanyTemplateChooser" >
            <button @click="toggleShowCompanyTemplateChooser()" class="tw-absolute tw-top-2 tw-right-2 tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-bg-grey-200 tw-rounded-lg hover:tw-bg-grey-800 tw-transition-colors">
              X
            </button>
            <h3 class="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest tw-mb-5">
              Choose a template from the Company Template Library
              
            </h3>
            
            <div class="tw-pr-2">
              <div v-if="loadingTemplates" class="tw-text-center tw-py-4">
                <font-awesome-icon icon="circle-notch" spin class="tw-text-slate-300" />
              </div>
              <div v-else class="tw-space-y-3">
                <div v-for="tpl in templates" :key="tpl.id" @click="selectLibraryTemplate(tpl)" :class="['tw-group tw-flex tw-items-center tw-gap-4 tw-p-3.5 tw-rounded-2xl tw-cursor-pointer tw-transition-all tw-border',
                  currentTemplate?.id === tpl.id
                    ? 'tw-bg-white tw-border-slate-900 tw-shadow-md'
                    : 'tw-bg-white tw-border-slate-200 hover:tw-shadow-sm hover:tw-border-slate-300']">

                  <div :class="['tw-w-10 tw-h-10 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shrink-0',
                    currentTemplate?.id === tpl.id ? 'tw-bg-slate-900 tw-text-white' : 'tw-bg-slate-50 tw-text-slate-400']">
                    <font-awesome-icon icon="file-pdf" />
                  </div>

                  <div class="tw-min-w-0 tw-flex-1 tw-flex tw-flex-col tw-justify-center">

                    <div class="tw-flex tw-items-center tw-gap-2">
                      <p class="tw-text-xs tw-font-bold tw-truncate tw-text-slate-900 tw-leading-none">
                        {{ tpl.name }}
                      </p>

                      <div v-if="tpl.isDefault"
                        class="tw-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded-md tw-shrink-0">
                        <font-awesome-icon icon="star" class="tw-text-blue-600 tw-text-[7px]" />
                        <span class="tw-text-[8px] tw-font-black tw-text-blue-600 tw-uppercase tw-tracking-wider">
                          Default
                        </span>
                      </div>
                    </div>

                    <div class="tw-flex tw-items-center tw-gap-2 tw-mt-1.5">
                      <span
                        class="tw-text-[9px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-bg-slate-100 tw-text-slate-500 tw-rounded tw-uppercase tw-tracking-tight tw-leading-none">
                        {{ tpl.modelType }}
                      </span>

                      <template v-if="tpl.id === currentTemplate?.id">
                        <span class="tw-w-1 tw-h-1 tw-bg-slate-300 tw-rounded-full"></span>
                        <span class="tw-text-[9px] tw-text-slate-400 tw-font-medium tw-leading-none">
                          Active Template
                        </span>
                      </template>
                    </div>
                  </div>

                  <button @click="handleDeleteTemplate($event, tpl)"
                    class="tw-opacity-0 group-hover:tw-opacity-100 tw-p-2 tw-text-slate-300 hover:tw-text-red-500 tw-transition-all tw-shrink-0">
                    <font-awesome-icon icon="trash" size="sm" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="tw-p-6 tw-flex-none template-reveal-animation tw-w-full tw-relative" v-if="isShowTemplateUploader">
            <button @click="toggleShowTemplateUploader()" class="tw-absolute tw-top-2 tw-right-2 tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-bg-grey-200 tw-rounded-lg hover:tw-bg-grey-800 tw-transition-colors">
              X
            </button>
            <h3 class="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest tw-mb-5">
              Upload Template
            </h3>
            <InputField v-model="newTemplateName" placeholder="Template Name" label="Name" />
            <DropdownList v-model="newModelType" :options="['TRIP', 'ORDER', 'DRIVER', 'BL']" label="Data Context" />
            <p class="tw-text-[10px] tw-leading-snug tw-text-slate-500">
              TRIP = dispatch/trip data. ORDER = delivery order data. DRIVER = driver profile data. BL = bill of lading.
            </p>

            <div class="tw-relative tw-group">
              <input type="file" accept="application/pdf"
                class="tw-absolute tw-inset-0 tw-opacity-0 tw-cursor-pointer tw-z-10" @change="handleFileChange" />
              <div
                class="tw-border-2 tw-border-dashed tw-border-slate-200 tw-bg-white tw-rounded-2xl tw-p-5 tw-text-center group-hover:tw-border-blue-400 tw-transition-colors">
                <font-awesome-icon :icon="selectedFile ? 'file-circle-check' : 'cloud-arrow-up'"
                  :class="selectedFile ? 'tw-text-green-500' : 'tw-text-slate-400'" size="lg" />
                <p class="tw-text-[11px] tw-mt-2 tw-font-semibold tw-text-slate-700 tw-truncate">
                  {{ selectedFile ? selectedFile.name : 'Select PDF Template' }}
                </p>
              </div>
            </div>

            <Button @click="handleUpload" :loading="uploading" :disabled="!canUpload"
              class="tw-w-full tw-h-11 tw-bg-slate-900 tw-text-white tw-rounded-xl tw-text-[11px] tw-font-black tw-uppercase tw-tracking-widest hover:tw-bg-slate-800 tw-transition-all tw-flex tw-items-center tw-justify-center tw-gap-3 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-shadow-sm">
              Upload Template
            </Button>
          </div>
        </div>

        <div class="tw-p-6 tw-border-b tw-border-slate-200 tw-flex-none tw-text-left" v-if="isShowTemplateFieldsCopy && currentTemplate && templates.length > 0">
          <h3 class="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-widest">
              Copy Fields from Another Template
            </h3>
            <p class="tw-text-[10px] tw-leading-snug tw-text-slate-500 tw-mb-2 tw-whitespace-normal">
              Copy template fields positions from another template into the current template (and overwrite any existing positions).
            </p>
          <DropdownList
            @update:modelValue="handleCopyTemplateFields($event)"
            :label="'label'"
            :placeholder="'Select a template'"
            :options="templates
              .filter(t => t.modelType === currentTemplate.modelType && Number(t.id) !== Number(currentTemplate.id))
              .map(t => ({ value: t.id, label: t.name }))"
          />
        </div>

        <div class="tw-p-6 tw-border-b tw-border-slate-200 tw-flex-none tw-text-left" v-if="currentTemplate && templates.length > 0">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h3 class="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-widest">
              Add Data Fields
            </h3>
            <span 
              @click="undo()" 
              class="tw-cursor-pointer" 
              aria-label="Undo"
              title="Und"
            >
              <font-awesome-icon icon="fa-arrow-rotate-back" />
            </span>
            <span
              @click="redo()"
              class="tw-cursor-pointer"
              aria-label="Redo"
              title="Redo"
            >
              <font-awesome-icon icon="fa-arrow-rotate-forward" />
            </span>
            <span 
              @click="exportPlacedFieldsToJson()"
              class="tw-cursor-pointer"
              title="export"
              aria-label="Export placed fields as JSON"
            >
              <font-awesome-icon icon="fa-arrow-up-from-bracket" />
            </span>
            <span 
              @click="importPlacedFieldsJson()" 
              class="tw-cursor-pointer" 
              title="import" 
              aria-label="Import placed fields from JSON"
            >
              <font-awesome-icon icon="fa-arrow-up-from-bracket" style="transform: rotate(180deg)" />
            </span>

            <span class="tw-text-[10px] tw-font-bold tw-text-blue-500 tw-bg-blue-50 tw-px-1.5 tw-py-0.5 tw-rounded">
              {{ filteredKeys.length }}
            </span>
          </div>

          <div class="tw-mb-4">
            <SearchInput v-model="searchQuery" placeholder="Search fields (e.g. driver, order...)" class="tw-w-full" />
          </div>

          <div class="tw-max-h-[28vh] tw-overflow-y-auto tw-pr-2 custom-scrollbar">
            <div class="tw-grid tw-grid-cols-1 tw-gap-2.5">
              <div v-for="key in filteredKeys" :key="key" @click="selectFieldKey(key)" draggable="true"
                @dragstart="onSidebarDragStart(key, $event)"
                :class="['tw-px-4 tw-py-3 tw-rounded-xl tw-text-xs tw-font-semibold tw-cursor-grab active:tw-cursor-grabbing tw-border tw-transition-all',
                  key === 'static.text' ? 'tw-bg-emerald-50 tw-border-emerald-200 tw-text-emerald-700 tw-mb-4' : '',
                  key === 'custom.*' ? 'tw-bg-purple-50 tw-border-purple-200 tw-text-purple-700' : '',
                  activeKey === key && key !== 'static.text' && key !== 'custom.*' ? 'tw-bg-white tw-border-blue-600 tw-text-blue-600 tw-shadow-sm' : '',
                  activeKey === key && key === 'static.text' ? 'tw-border-emerald-600 tw-shadow-sm' : '',
                  activeKey === key && key === 'custom.*' ? 'tw-border-purple-600 tw-shadow-sm' : '',
                  activeKey !== key && key !== 'static.text' && key !== 'custom.*' ? 'tw-bg-white tw-border-slate-200 tw-text-slate-700 hover:tw-border-slate-300' : '']">
                <font-awesome-icon icon="grip-vertical" class="tw-mr-3 tw-opacity-30" />
                {{ key }}
              </div>

              <div v-if="filteredKeys.length === 0" class="tw-py-4 tw-text-center">
                <p class="tw-text-[10px] tw-text-slate-400 tw-italic">No fields found</p>
              </div>
            </div>
          </div>
        </div>


      </aside>

      <div
        class="tw-flex-1 tw-bg-slate-100 tw-overflow-auto tw-relative tw-flex tw-items-start tw-justify-center tw-p-4 md:tw-p-8 lg:tw-p-12">
        <div v-if="!pdfPreviewUrl" class="tw-mt-20 tw-text-center">
          <div
            class="tw-w-24 tw-h-24 tw-bg-white tw-rounded-[2.5rem] tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-8 tw-shadow-sm tw-border tw-border-slate-200">
            <font-awesome-icon icon="file-pdf" class="tw-text-slate-200 tw-text-4xl" />
          </div>
          <h4 class="tw-text-xl tw-font-bold tw-text-slate-900">Ready to edit?</h4>
          <p class="tw-text-sm tw-text-slate-500 tw-mt-2 tw-max-w-xs tw-mx-auto">
            Select a template from your library or upload a new one to start mapping your fields.
          </p>
        </div>
        <div v-else class="tw-flex tw-flex-col tw-items-center tw-w-full tw-min-h-0 tw-relative">
          <div v-if="isPdfLoading" class="tw-absolute tw-top-50p tw-left-50p tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2">
            <div uk-spinner>Loading...</div>
          </div>
          <div class="tw-relative tw-shadow-2xl tw-shadow-slate-900/10 tw-bg-white tw-rounded-sm tw-mt-0 tw-mb-32"
            :style="canvasWrapperStyle" ref="pdfContainer" @mousedown="handleCanvasClick" @mousemove="onDrag"
            @mouseup="stopDrag" @mouseleave="stopDrag" @dragover.prevent @drop="onSidebarDrop">
            <canvas ref="pdfCanvas" class="tw-w-full tw-h-auto"></canvas>

            <div v-if="showGrid" class="grid-overlay"></div>

            <div v-for="(field, index) in placedFields" :key="index">
              <template v-if="field.page === currentPage">
                <div @mousedown.stop="startDrag(index, $event)" @click.stop="selectField(index)" :class="['tw-absolute tw-cursor-move tw-group tw-select-none',
                  selectedFieldIndex === index ? 'tw-ring-2 tw-ring-offset-2 tw-ring-blue-500 tw-rounded-lg' : '']"
                  :style="{
                    left: (field.x * 100) + '%',
                    top: (field.y * 100) + '%',
                    fontSize: 'clamp(8px, 1.1cqi, 14px)',
                    transform: 'translateY(-100%)',
                    zIndex: selectedFieldIndex === index ? 50 : 20
                  }">

                  <div :class="[
                    'tw-flex tw-items-center tw-gap-1.5 tw-px-2 tw-py-1 tw-rounded-lg tw-shadow-lg tw-transition-colors',
                    resolvedFieldType(field) === 'static' ? 'tw-bg-emerald-600 tw-text-white' : '',
                    resolvedFieldType(field) === 'dynamic' ? 'tw-bg-blue-600 tw-text-white' : '',
                    resolvedFieldType(field) === 'user_prompt' ? 'tw-bg-purple-600 tw-text-white' : ''
                  ]">
                    <font-awesome-icon :icon="resolvedFieldType(field) === 'static' ? 'font' : (resolvedFieldType(field) === 'user_prompt' ? 'keyboard' : 'database')"
                      class="tw-text-[10px] tw-opacity-70" />
                    <span class="tw-font-bold tw-whitespace-nowrap">
                      {{
                        resolvedFieldType(field) === 'static'
                          ? (field.static_value || 'Empty Text')
                          : (resolvedFieldType(field) === 'user_prompt'
                            ? getPromptDisplayLabel(field)
                            : field.field_key)
                      }}
                    </span>
                    <button @click.stop="removeField(index)" class="hover:tw-text-red-200 tw-ml-1">
                      <font-awesome-icon icon="xmark" />
                    </button>
                  </div>
                </div>
              </template>
            </div>
            <transition name="fade-slide">
              <div v-if="selectedFieldIndex !== null && !isDragging"
                class="tw-fixed tw-bottom-10 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-[70] tw-w-max tw-max-w-[90vw] tw-bg-white/80 tw-backdrop-blur-xl tw-rounded-3xl tw-shadow-[0_20px_50px_rgba(0,0,0,0.1)] tw-border tw-border-slate-200/50 tw-p-3 tw-flex tw-items-center tw-gap-5 tw-pointer-events-auto"
                @mousedown.stop>

                <div class="tw-pl-3 tw-pr-5 tw-border-r tw-border-slate-100">
                  <p class="tw-text-[9px] tw-font-black tw-text-slate-400 tw-uppercase tw-tracking-[0.15em]">Field</p>
                  <p class="tw-text-[11px] tw-font-bold tw-text-slate-900 tw-font-mono tw-mt-0.5">
                    {{ placedFields[selectedFieldIndex].field_key }}
                  </p>
                </div>

                <div v-if="resolvedFieldType(placedFields[selectedFieldIndex]) === 'static'"
                  class="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-mr-4">
                  <input type="text" v-model="placedFields[selectedFieldIndex].static_value" placeholder="Text..."
                    class="tw-w-32 tw-px-3 tw-py-2 tw-bg-slate-100 tw-rounded-xl tw-text-xs tw-font-bold tw-outline-none" />
                </div>

                <div v-if="resolvedFieldType(placedFields[selectedFieldIndex]) === 'user_prompt'" class="tw-flex tw-items-center tw-gap-2">
                  <label class="tw-text-[10px] tw-font-bold tw-text-slate-500">Label</label>
                  <input
                    type="text"
                    v-model="placedFields[selectedFieldIndex].prompt_label"
                    @blur="syncPromptFieldKeyFromLabel(selectedFieldIndex)"
                    placeholder="My Date Field"
                    class="tw-w-40 tw-px-2 tw-py-1 tw-bg-slate-100 tw-rounded-lg tw-text-[11px] tw-font-semibold tw-outline-none"
                  />
                </div>

                <div class="tw-flex tw-items-center tw-gap-2">
                  <input type="range" min="6" max="36" v-model.number="placedFields[selectedFieldIndex].font_size"
                    class="tw-w-24 accent-slate-900" />
                  <input
                    type="number"
                    min="6"
                    max="36"
                    v-model.number="placedFields[selectedFieldIndex].font_size"
                    @keydown.enter.prevent="normalizeSelectedFieldFontSize"
                    @blur="normalizeSelectedFieldFontSize"
                    class="tw-w-14 tw-px-2 tw-py-1 tw-bg-slate-100 tw-rounded-lg tw-text-[11px] tw-font-semibold tw-outline-none"
                  />
                  <span class="tw-text-[10px] tw-font-bold tw-text-slate-500">px</span>
                </div>

                <div class="tw-flex tw-items-center tw-gap-2">
                  <label class="tw-text-[10px] tw-font-bold tw-text-slate-500">Max Chars</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="-"
                    v-model.number="placedFields[selectedFieldIndex].max_chars_per_line"
                    class="tw-w-16 tw-px-2 tw-py-1 tw-bg-slate-100 tw-rounded-lg tw-text-[11px] tw-font-semibold tw-outline-none"
                  />
                </div>

                <div class="tw-flex tw-items-center tw-gap-2">
                  <label class="tw-text-[10px] tw-font-bold tw-text-slate-500">Max Lines</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="-"
                    v-model.number="placedFields[selectedFieldIndex].max_lines"
                    class="tw-w-14 tw-px-2 tw-py-1 tw-bg-slate-100 tw-rounded-lg tw-text-[11px] tw-font-semibold tw-outline-none"
                  />
                </div>

                <button @click="placedFields[selectedFieldIndex].is_bold = !placedFields[selectedFieldIndex].is_bold"
                  :class="[placedFields[selectedFieldIndex].is_bold ? 'tw-bg-slate-900 tw-text-white' : 'tw-text-slate-400', 'tw-w-8 tw-h-8 tw-rounded-lg tw-transition-all']">
                  <font-awesome-icon icon="bold" />
                </button>

                <button @click="selectedFieldIndex = null" class="tw-text-slate-300 hover:tw-text-slate-900">
                  <font-awesome-icon icon="xmark" />
                </button>
              </div>
            </transition>
          </div>
          <div v-if="numPages > 1"
            class="tw-sticky tw-bottom-4 tw-z-[60] tw-w-full tw-flex tw-justify-center ttw-pointer-events-none">
            <div
              class="tw-flex tw-items-center tw-gap-2 tw-bg-white tw-px-3 tw-py-1 tw-rounded-xl tw-shadow-lg tw-shadow-slate-900/5 tw-border tw-border-slate-200 tw-pointer-events-auto">
              <button @click="changePage(-1)" :disabled="currentPage === 1"
                class="tw-p-1 tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center hover:tw-bg-slate-100 tw-rounded-lg disabled:tw-opacity-30 tw-transition-colors">
                <font-awesome-icon icon="chevron-left" />
              </button>

              <span
                class="tw-text-xs tw-font-bold tw-text-slate-600 tw-min-w-[70px] tw-text-center tw-select-none tw-uppercase tw-tracking-wider">
                {{ currentPage }} / {{ numPages }}
              </span>

              <button @click="changePage(1)" :disabled="currentPage === numPages"
                class="tw-p-1 tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center hover:tw-bg-slate-100 tw-rounded-lg disabled:tw-opacity-30 tw-transition-colors">
                <font-awesome-icon icon="chevron-right" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw } from 'vue';
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs';
import Button from '@/components/core/Button.vue';
import InputField from '@/components/core/Input/InputField.vue';
import DropdownList from '@/components/core/DropdownList.vue';
import SearchInput from '@/components/core/SearchInput.vue';

// GraphQL Imports
import SaveTemplateFieldsMutation from '@/gql/document_builder/SaveTemplateFields.gql';
import SetTemplateDefaultMutation from '@/gql/document_builder/SetTemplateDefault.gql';
import GetCompanyTemplatesQuery from '@/gql/document_builder/GetCompanyTemplates.gql';
import GetTemplateFieldsQuery from '@/gql/document_builder/GetTemplateFields.gql';
import DeleteDocumentTemplateMutation from '@/gql/document_builder/DeleteDocumentTemplate.gql';
import CopyTemplateFieldsMutation from '@/gql/document_builder/CopyTemplateFields.gql';
import GetOrderQuery from '@/gql/orders/queries/Order.gql';
import GetContainerQuery from '@/gql/containers/Container.gql';

import { pushToast } from '@/lib/toast';
import { useAuthStore } from '@/stores/auth';

export default defineComponent({
  name: 'DocumentTemplateEditor',
  components: { Button, InputField, DropdownList, SearchInput },
  props: ['isOpen'],
  emits: ['close'],
  data() {
    return {
      activeKey: 'driver.name',
      placedFields: [] as any[],
      selectedFieldIndex: null as number | null,
      clipboardField: null as any | null,
      undoStack: [] as any[][],
      redoStack: [] as any[][],
      preDragSnapshot: null as any[] | null,
      availableKeys: [
        'static.text',

        'company.name',
        'company.address',
        'company.phone',
        'company.email',

        'driver.name',
        'driver.phone',
        
        'order.number',
        'order.bl_number',
        'order.booking_number',
        'order.bl_or_booking_number',

        'order.reference_number',
        'order.ship_line',
        'order.vessel',
        
        'order.pickup.name',
        'order.pickup.address',
        'order.delivery.name',
        'order.delivery.address',
        'order.delivery.city_state',
        

        'container.number',
        'container.index',
        'container.seal',
        'container.size_type',
        'container.terminal_appointment_date',
        'container.delivery_appointment_date',
        'container.appointment_date',
        'container.notes',
        'container.return_location',
        'container.return_location_address',
        'container.genset_needed',
        'container.genset_needed_with_word_geneset',
        'container.genset_number',
        'container.genset_number_with_pound',
        'container.genset_temperature',
        'container.genset_vents_open_percent',
        'container.genset',
        'container.genset_with_word_geneset',
        
        // 'cargo.items'
        'cargo.*.qty',
        'cargo.*.pkg',
        'cargo.*.haz',
        'cargo.*.weight',
        'cargo.*.description',
        
        'trip.type',
        'trip.chassis',
        'trip.scheduled_date',
        'trip.notes',
        'trip.leg_index',

        'trip.delivery.name',
        'trip.delivery.address',
        'trip.pickup.name',
        'trip.pickup.address',
      ],
      currentTemplate: null as any | null,
      pdfPreviewUrl: '',
      isPdfLoading: false,
      templates: [] as any[],
      copyFieldsFromTemplate: null as Object | null,
      loadingTemplates: false,
      pdfWidth: 0,
      pdfHeight: 0,
      saving: false,
      newTemplateName: '',
      newModelType: 'ORDER',
      uploading: false,
      selectedFile: null as any,
      renderScale: 2,
      isDragging: false,
      draggedIndex: null as number | null,
      showGrid: false,
      snapToGrid: false,
      searchQuery: '',
      currentPage: 1,
      numPages: 0,
      pdfDoc: null as any,
      pdfBlob: null as any,
      isShowCompanyTemplateChooser: false,
      isShowTemplateUploader: false,
      isShowTemplateFieldsCopy: false,
      showPreviewDropdown: false,
      sampleOrderId: '' as string,
      sampleOrder: null as any,
      sampleContainerId: null as string | null,
      sampleContainer: null as any,
      sampleTripId: null as string | null,
    };
  },
  computed: {
    canUpload(): boolean {
      return !!(this.newTemplateName && this.selectedFile && !this.uploading);
    },

    canvasWrapperStyle(): any {
      if (this.pdfWidth === 0) return { width: '100%' };
      return {
        width: '100%',
        maxWidth: '1200px',
        aspectRatio: `${this.pdfWidth} / ${this.pdfHeight}`,
        position: 'relative',
        backgroundColor: '#fff',
        containerType: 'inline-size',
      };
    },

    sortedTrips(): any[] {
      const trips = this.sampleContainer?.trips || [];
      return [...trips].sort((a: any, b: any) => (a.legId ?? 0) - (b.legId ?? 0));
    },

    filteredKeys(): string[] {
      const base = this.availableKeys.filter((k: string) => k !== 'static.text');
      const keys = ['static.text', 'custom.*', ...base];
      if (!this.searchQuery) return keys;
      const query = this.searchQuery.toLowerCase();
      return keys.filter(key => key.toLowerCase().includes(query));
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        window.addEventListener('keydown', (this as any).onKeyDown);
        (this as any).fetchCompanyTemplates();
      } else {
        window.removeEventListener('keydown', (this as any).onKeyDown);
      }
    },
    currentTemplate(newTpl) {
      if (newTpl) {
        this.pdfPreviewUrl = `/api/document-builder/template-pdf/${newTpl.id}`;
        this.$nextTick(() => (this as any).initPdf());
        (this as any).fetchTemplateFields();
      }
    },
    sampleOrderId(newVal) {
      this.sampleContainerId = null;
      this.sampleTripId = null;
    },
    sampleOrder(newVal) {
      if (newVal) {
        this.sampleContainerId = newVal.containers?.[0]?.id || null;
      }
    },
    sampleContainerId(newVal) {
      this.sampleTripId = null;
    },
    sampleContainer(newVal) {
      if (newVal) {
        this.sampleTripId = newVal.trips?.[0]?.id || null;
      }
    },

  },
  mounted() {
    if (this.isOpen) {
      window.addEventListener('keydown', (this as any).onKeyDown);
      this.fetchCompanyTemplates();
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', (this as any).onKeyDown);
  },
  methods: {
    selectFieldKey(key: string) {
      this.activeKey = key;
    },
    resolvedFieldType(field: any): string {
      if (field?.field_type) return field.field_type;
      // backwards compatible (for template fields BEFORE the user_prompt type/feature was added), where field_type is NULL
      return field?.field_key === 'static.text' ? 'static' : 'dynamic';
    },
    getPromptDisplayLabel(field: any): string {
      const explicitLabel = String(field?.prompt_label || '').trim();
      if (explicitLabel) return explicitLabel;
      const key = String(field?.field_key || '');
      return key.startsWith('custom.') ? key.slice('custom.'.length) : key;
    },
    slugifyPromptSegment(value: string): string {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    },
    getUniqueCustomPromptKey(baseSegment: string, ignoreIndex: number | null = null): string {
      const safeSegment = this.slugifyPromptSegment(baseSegment) || 'user_prompt';
      const baseKey = `custom.${safeSegment}`;
      let candidate = baseKey;
      let suffix = 2;
      const exists = (key: string) => this.placedFields.some((f: any, idx: number) => {
        if (idx === ignoreIndex) return false;
        return this.resolvedFieldType(f) === 'user_prompt' && String(f.field_key || '') === key;
      });
      while (exists(candidate)) {
        candidate = `${baseKey}_${suffix}`;
        suffix += 1;
      }
      return candidate;
    },
    makeCustomPromptKey(rawLabel: string): string {
      return this.getUniqueCustomPromptKey(rawLabel, null);
    },
    getNextUserPromptPosition(page: number): { x: number; y: number } {
      const existingOnPage = (this.placedFields || []).filter((f: any) =>
        this.resolvedFieldType(f) === 'user_prompt' && Number(f.page || 1) === Number(page)
      ).length;
      const col = existingOnPage % 5;
      const row = Math.floor(existingOnPage / 5);
      return {
        x: Math.min(0.1 + col * 0.16, 0.9),
        y: Math.min(0.12 + row * 0.09, 0.92),
      };
    },
    addUserPromptField(initialX?: number, initialY?: number) {
      const label = `User Input Field ${Math.round(Math.random() * 10000)}`;
      const key = this.getUniqueCustomPromptKey(label, null);
      const page = Number(this.currentPage || 1);
      const fallbackPos = this.getNextUserPromptPosition(page);
      this.pushHistory();
      this.placedFields.push({
        field_key: key,
        prompt_label: label,
        field_type: 'user_prompt',
        x: Number.isFinite(Number(initialX)) ? Math.max(0, Math.min(Number(initialX), 1)) : fallbackPos.x,
        y: Number.isFinite(Number(initialY)) ? Math.max(0, Math.min(Number(initialY), 1)) : fallbackPos.y,
        page,
        font_size: 12,
        max_chars_per_line: null,
        max_lines: null,
        is_bold: false,
        static_value: '',
      });
      // Keep the custom tool selected to allow rapid multi-placement,
      // same behavior as static/dynamic fields.
      this.selectedFieldIndex = null;
    },
    syncPromptFieldKeyFromLabel(index: number | null) {
      if (index === null) return;
      const field = this.placedFields[index];
      if (!field || this.resolvedFieldType(field) !== 'user_prompt') return;
      field.field_key = this.getUniqueCustomPromptKey(field.prompt_label || '', index);
    },
    toggleShowTemplateFieldsCopy() {
      this.isShowTemplateFieldsCopy = !this.isShowTemplateFieldsCopy;
      this.isShowCompanyTemplateChooser = false;
      this.isShowTemplateUploader = false;
    },
    toggleShowTemplateUploader() {
      this.isShowTemplateUploader = !this.isShowTemplateUploader;
      this.isShowCompanyTemplateChooser = false;
      this.isShowTemplateFieldsCopy = false;
    },
    toggleShowCompanyTemplateChooser() {
      this.isShowCompanyTemplateChooser = !this.isShowCompanyTemplateChooser;
      this.isShowTemplateUploader = false;
      this.isShowTemplateFieldsCopy = false;
    },
    clonePlacedFields(fields: any[]) {
      return (fields || []).map((f: any) => ({ ...f }));
    },
    pushHistory(snapshot?: any[]) {
      const snap = snapshot ? this.clonePlacedFields(snapshot) : this.clonePlacedFields(this.placedFields);
      this.undoStack.push(snap);
      if (this.undoStack.length > 200) this.undoStack.shift();
      this.redoStack = [];
    },
    undo() {
      if (!this.undoStack.length) return;
      this.redoStack.push(this.clonePlacedFields(this.placedFields));
      const prev = this.undoStack.pop();
      this.placedFields = this.clonePlacedFields(prev);
      this.selectedFieldIndex = null;
    },
    redo() {
      if (!this.redoStack.length) return;
      this.undoStack.push(this.clonePlacedFields(this.placedFields));
      const next = this.redoStack.pop();
      this.placedFields = this.clonePlacedFields(next);
      this.selectedFieldIndex = null;
    },
    selectField(index: number) {
      this.selectedFieldIndex = index;
    },
    normalizeSelectedFieldFontSize() {
      if (this.selectedFieldIndex === null) return;
      const field = this.placedFields[this.selectedFieldIndex];
      if (!field) return;

      const parsed = Number(field.font_size);
      const safe = Number.isFinite(parsed) ? parsed : 12;
      field.font_size = Math.max(6, Math.min(36, safe));
    },
    copySelectedField() {
      if (this.selectedFieldIndex === null) return;
      const f = this.placedFields[this.selectedFieldIndex];
      if (!f) return;
      this.clipboardField = { ...f };
    },
    pasteField() {
      if (!this.clipboardField || !this.pdfWidth) return;
      this.pushHistory();

      const offset = 0.02;
      const nf = {
        ...this.clipboardField,
        x: Math.min(this.clipboardField.x + offset, 1),
        y: Math.min(this.clipboardField.y + offset, 1)
      };

      this.placedFields.push(nf);
      this.selectedFieldIndex = this.placedFields.length - 1;
    },
    exportPlacedFieldsToJson() {
      const jsonStr = JSON.stringify(this.placedFields);
      navigator.clipboard.writeText(jsonStr);
      alert("Copied to clipboard. You can now paste into another template or  another Contrak window");
    },
    importPlacedFieldsJson() {
      const jsonStr = prompt("Paste fields export JSON here to import:");
      if (!jsonStr) return;
      try {
        const parsed = JSON.parse(jsonStr);
        this.placedFields = parsed;
      } catch (e) {
        alert("Invalid JSON");
      }
    },
    isTypingTarget(e: KeyboardEvent): boolean {
      const t = e.target as any;
      if (!t) return false;
      const tag = String(t.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || t.isContentEditable;
    },
    onKeyDown(e: KeyboardEvent) {
      if (!this.isOpen || this.isTypingTarget(e)) return;

      const key = String(e.key || '').toLowerCase();
      const mod = e.ctrlKey || e.metaKey;

      if (mod && key === 'z') {
        e.preventDefault();
        e.shiftKey ? this.redo() : this.undo();
        return;
      }
      if (mod && key === 'y') { e.preventDefault(); this.redo(); return; }
      if (mod && key === 'c') { e.preventDefault(); this.copySelectedField(); return; }
      if (mod && key === 'v') { e.preventDefault(); this.pasteField(); return; }

      if ((key === 'delete' || key === 'backspace') && this.selectedFieldIndex !== null) {
        e.preventDefault();
        this.removeField(this.selectedFieldIndex);
      }
    },
    async fetchCompanyTemplates() {
      this.loadingTemplates = true;
      try {
        const { data } = await this.$apollo.query({
          query: GetCompanyTemplatesQuery,
          fetchPolicy: 'network-only'
        });
        this.templates = data.companyDocumentTemplates;
      } catch (e) { pushToast("Could not load library", "error", 1500); } finally { this.loadingTemplates = false; }
    },
    async fetchTemplateFields() {
      if (!this.currentTemplate) return;
      try {
        const { data } = await this.$apollo.query({
          query: GetTemplateFieldsQuery,
          variables: { templateId: Number(this.currentTemplate.id) },
          fetchPolicy: 'network-only',
        });
        this.placedFields = data.templateFields.map((f: any) => ({
          field_key: f.fieldKey,
          field_type: f.fieldType || (f.fieldKey === 'static.text' ? 'static' : 'dynamic'),
          prompt_label: f.promptLabel || '',
          x: f.x,
          y: f.y,
          page: f.page || 1,
          font_size: f.fontSize || 12,
          max_chars_per_line: f.maxCharsPerLine ?? null,
          max_lines: f.maxLines ?? null,
          is_bold: f.isBold || false,
          static_value: f.staticValue || '',
        }));
        // the old selection points into the previous template's fields
        this.selectedFieldIndex = null;
      } catch (e) { console.error("Failed to fetch fields", e); }
    },
    selectLibraryTemplate(tpl: any) {
      this.currentTemplate = tpl;
      pushToast(`Template ${tpl.name} loaded`, "success", 500);
    },
    async initPdf() {
      if (!this.pdfPreviewUrl) return;
      try {
        this.isPdfLoading = true;
        const token = useAuthStore().token;
        const response = await fetch(this.pdfPreviewUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const blob = await response.blob();
        this.pdfBlob = markRaw(blob);
        const pdfArrayBuffer = await blob.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({
          data: pdfArrayBuffer
        });
        const pdf = await loadingTask.promise;
        this.pdfDoc = markRaw(pdf);

        this.numPages = this.pdfDoc.numPages;

        await this.renderPage(this.currentPage);

        this.isShowCompanyTemplateChooser = false;

        if (this.placedFields.length > 0) this.fetchTemplateFields();
        
      } catch (err) { 
        console.error('PDF Render Error:', err); 
        pushToast('Failed to load PDF', 'error', 1500);
      } finally {
        this.isPdfLoading = false;
      }
    },

    async renderPage(pageNumber: number) {
      if (!this.pdfDoc) return;
      try {
        const page = await this.pdfDoc.getPage(pageNumber);
        const viewportOrig = page.getViewport({ scale: 1 });
        this.pdfWidth = viewportOrig.width;
        this.pdfHeight = viewportOrig.height;

        const canvas = this.$refs.pdfCanvas as HTMLCanvasElement;
        const context = canvas.getContext('2d');
        const viewport = page.getViewport({ scale: this.renderScale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context!, viewport }).promise;
      } catch (err) {
        console.error('Render Page Error:', err);
      }
    },

    async changePage(step: number) {
      const newPage = this.currentPage + step;
      if (newPage >= 1 && newPage <= this.numPages) {
        this.currentPage = newPage;
        this.selectedFieldIndex = null;
        await this.renderPage(this.currentPage);
      }
    },

    handleFileChange(e: any) {
      const file = e.target.files[0];
      if (file) this.selectedFile = file;
      e.target.value = '';
    },
    async handleUpload() {
      if (!this.selectedFile || !this.newTemplateName) return;
      this.uploading = true;
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      formData.append('name', this.newTemplateName);
      formData.append('model_type', this.newModelType);
      try {
        const res = await fetch(`${process.env.VUE_APP_SERVER_URL}/api/document-builder/upload-template`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${useAuthStore().token}` },
          body: formData
        });
        const result = await res.json();
        if (result.success) {
          pushToast("Uploaded!", "success", 500);
          await this.fetchCompanyTemplates();
          // the last template to upload becomes the current one
          this.currentTemplate = this.templates[this.templates.length - 1];
          this.selectedFile = null;
          this.newTemplateName = '';
          this.toggleShowTemplateUploader();
        }
      } catch (e) { pushToast("Upload failed", "error", 1500); } finally { this.uploading = false; }
    },
    async handleDownloadTemplate() {

      if (!this.currentTemplate) return;
      
      const tpl = this.currentTemplate;
      try {
        const url = window.URL.createObjectURL(this.pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${tpl.name.trim().replace(/\s+/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);
        link.remove();
        pushToast("PDF downloaded", "success", 500);
      } catch (e: any) { pushToast("Download failed", "error", 1500); }
    },
    async handleDeleteTemplate(e: Event, tpl: any) {
      e.stopPropagation();
      if (!confirm(`Delete "${tpl.name}"?`)) return;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: DeleteDocumentTemplateMutation,
          variables: { templateId: Number(tpl.id) }
        });
        if (data.deleteDocumentTemplate.success) {
          pushToast("Template deleted", "success", 500);
          await this.fetchCompanyTemplates();
          this.currentTemplate = null;
        }
      } catch (e) { pushToast("Delete failed", "error", 1500); }
    },
    async handleCopyTemplateFields(sourceTplFromDropDown: any) {
      const targetTpl = this.currentTemplate; // current template
      const sourceTpl = this.templates.find(t => t.id === sourceTplFromDropDown?.value); // source template
      if (!confirm(`Clear template fields of "${targetTpl.name}" and copy template fields of "${sourceTpl.name}" to "${targetTpl.name}"?`)) return;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: CopyTemplateFieldsMutation,
          variables: { sourceTemplateId: Number(sourceTpl.id), targetTemplateId: Number(targetTpl.id) }
        });
        if (data.copyTemplateFields.success) {
          this.toggleShowTemplateFieldsCopy();
          pushToast("Template fields cleared and copied", "success", 500);
          await this.fetchTemplateFields();
        }
      } catch (e) { pushToast("Copy failed: " + (e as Error)?.message, "error", 1500); }
    },
    //
    async handlePreview() {
      if (!this.sampleOrderId || !this.sampleContainerId) {
        return pushToast("Please select an order and container", "warning", 1500);
      }
      try {
        const saveSuccess = await this.saveFields();
        if (saveSuccess === false) return;

        if (!this.currentTemplate?.id) return pushToast("Save template first", "warning", 1500);

        const token = useAuthStore().token;
        let url;
        const promptValues: Record<string, string> = {};
        (this.placedFields || [])
          .filter((f: any) => this.resolvedFieldType(f) === 'user_prompt')
          .forEach((f: any) => {
            const label = this.getPromptDisplayLabel(f);
            const value = window.prompt(`Preview value for "${label}"`, '');
            promptValues[f.field_key] = value == null ? '' : String(value);
          });
        if (this.currentTemplate.modelType === 'BL') {
          url = `${process.env.VUE_APP_SERVER_URL}/api/document-builder/generate-bl/order/${this.sampleOrderId}/container/${this.sampleContainerId}`
        } else if (this.currentTemplate.modelType === 'TRIP') {
          url = `${process.env.VUE_APP_SERVER_URL}/api/document-builder/generate-dispatch/${this.sampleOrderId}/${this.sampleContainerId}/${this.sampleTripId}`
        } else {
          pushToast(`Printing/Preview for template type: ${this.currentTemplate.modelType} is not defined yet`, "error", 4000)
          return;
        }
        if (url && Object.keys(promptValues).length > 0) {
          url += `?promptValues=${encodeURIComponent(JSON.stringify(promptValues))}`;
        }
        if (!url) {
          pushToast(`Template type ${this.currentTemplate.modelType} unknown; can't do preview`, "error", 1500);
        }
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (!response.ok) throw new Error(await response.text());
        const blob = await response.blob();
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, '_blank');
        setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
      } catch (e: any) { pushToast("Preview failed: " + e.message, "error", 1500); }
    },

    handleCanvasClick(e: MouseEvent) {
      if (this.isDragging || !this.activeKey || !this.pdfWidth) return;
      const rect = (this.$refs.pdfContainer as HTMLElement).getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      if (this.activeKey === 'custom.*') {
        this.addUserPromptField(x, y);
        return;
      }
      this.pushHistory();
      this.placedFields.push({
        field_key: this.activeKey,
        field_type: this.activeKey === 'static.text' ? 'static' : 'dynamic',
        prompt_label: '',
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        page: Number(this.currentPage),
        font_size: 12,
        max_chars_per_line: null,
        max_lines: null,
        is_bold: false,
        static_value: this.activeKey === 'static.text' ? 'New Text' : '',
      });
      this.selectedFieldIndex = null;
    },
    startDrag(index: number, e: MouseEvent) {
      e.preventDefault();
      e.stopPropagation();
      this.isDragging = true;
      this.draggedIndex = index;
      this.selectedFieldIndex = index;
      this.preDragSnapshot = this.clonePlacedFields(this.placedFields);
    },
    onDrag(e: MouseEvent) {
      if (!this.isDragging || this.draggedIndex === null) return;

      const rect = (this.$refs.pdfContainer as HTMLElement).getBoundingClientRect();
      const field = this.placedFields[this.draggedIndex];

      let xPercent = (e.clientX - rect.left) / rect.width;
      let yPercent = (e.clientY - rect.top) / rect.height;

      if (this.snapToGrid) {
        const snapStep = 10;

        const xPx = xPercent * rect.width;
        const yPx = yPercent * rect.height;

        const snappedX = Math.round(xPx / snapStep) * snapStep;
        const snappedY = Math.round(yPx / snapStep) * snapStep;

        xPercent = snappedX / rect.width;
        yPercent = snappedY / rect.height;
      }

      field.x = Math.max(0, Math.min(xPercent, 1));
      field.y = Math.max(0, Math.min(yPercent, 1));
    },
    stopDrag() {
      if (!this.isDragging) return;
      this.isDragging = false; this.draggedIndex = null;
      if (this.preDragSnapshot && JSON.stringify(this.preDragSnapshot) !== JSON.stringify(this.placedFields)) {
        this.pushHistory(this.preDragSnapshot);
      }
      this.preDragSnapshot = null;
    },
    onSidebarDragStart(key: string, e: DragEvent) {
      if (e.dataTransfer) {
        e.dataTransfer.setData('field_key', key);
        e.dataTransfer.effectAllowed = 'copy';

        this.activeKey = key;
      }
    },
    onSidebarDrop(e: DragEvent) {
      e.preventDefault();
      const key = e.dataTransfer?.getData('field_key');
      if (!key || !this.pdfWidth) return;
      const rect = (this.$refs.pdfContainer as HTMLElement).getBoundingClientRect();
      const xPercent = (e.clientX - rect.left) / rect.width;
      const yPercent = (e.clientY - rect.top) / rect.height;
      if (key === 'custom.*') {
        this.addUserPromptField(xPercent, yPercent);
        return;
      }

      this.pushHistory();

      this.placedFields.push({
        field_key: key,
        field_type: key === 'static.text' ? 'static' : 'dynamic',
        prompt_label: '',
        x: Math.max(0, Math.min(xPercent, 1)),
        y: Math.max(0, Math.min(yPercent, 1)),
        page: Number(this.currentPage),
        font_size: 12,
        max_chars_per_line: null,
        max_lines: null,
        is_bold: false,
        static_value: key === 'static.text' ? 'New Text' : '',
      });

      this.selectedFieldIndex = this.placedFields.length - 1;
      this.activeKey = null;
    },
    removeField(index: number) {
      this.pushHistory();
      this.placedFields.splice(index, 1);
      if (this.selectedFieldIndex === index) {
        this.selectedFieldIndex = null;
      } else if (this.selectedFieldIndex !== null && this.selectedFieldIndex > index) {
        this.selectedFieldIndex--;
      }
    },
    async saveFields() {
      this.saving = true;
      try {
        this.placedFields.forEach((f: any, idx: number) => {
          if (this.resolvedFieldType(f) !== 'user_prompt') return;
          const label = String(f.prompt_label || '').trim() || this.getPromptDisplayLabel(f) || `User Input Field ${Math.round(Math.random() * 10000)}`;
          f.prompt_label = label;
          f.field_key = this.getUniqueCustomPromptKey(label, idx);
        });
        const fieldsInput = this.placedFields.map((f: any) => ({
          fieldKey: f.field_key,
          x: parseFloat(f.x.toFixed(4)),
          y: parseFloat(f.y.toFixed(4)),
          page: f.page || 1,
          fontSize: f.font_size || 12,
          maxCharsPerLine:
            Number.isFinite(Number(f.max_chars_per_line)) && Number(f.max_chars_per_line) > 0
              ? Number(f.max_chars_per_line)
              : null,
          maxLines:
            Number.isFinite(Number(f.max_lines)) && Number(f.max_lines) > 0
              ? Number(f.max_lines)
              : null,
          isBold: !!f.is_bold,
          staticValue: f.static_value || null,
        fieldType: this.resolvedFieldType(f),
        promptLabel: this.resolvedFieldType(f) === 'user_prompt' ? (f.prompt_label || f.field_key) : null,
        }));
        await this.$apollo.mutate({
          mutation: SaveTemplateFieldsMutation,
          variables: { templateId: Number(this.currentTemplate?.id), fields: fieldsInput }
        });
        pushToast("Saved!", "success", 500);
        await this.fetchCompanyTemplates();
        return true;
      } catch (e) {
        pushToast("Save failed", "error", 1500);
        return false;
      } finally { this.saving = false; }
    },
    async fetchSampleContainer() {
      const id = this.sampleContainerId;
      if (!id) return pushToast('Please select a container', 'warning', 1500);
      try {
        const { data } = await this.$apollo.query({
          query: GetContainerQuery,
          variables: { id },
          fetchPolicy: 'network-only',
        });
        this.sampleContainer = data.container;
        pushToast('Container loaded', 'success', 500);
      } catch (e) {
        pushToast('Failed to fetch container', 'error', 1500);
      }
    },
    async fetchSampleOrder() {
      const id = this.sampleOrderId;
      if (!id) return pushToast('Please enter an Order ID', 'warning', 1500);
      try {
        const { data } = await this.$apollo.query({
          query: GetOrderQuery,
          variables: { id },
          fetchPolicy: 'network-only',
        });
        this.sampleOrder = data.order;
        console.log(data.order)
        pushToast('Order loaded', 'success', 500);
      } catch (e) {
        pushToast('Failed to fetch order', 'error', 1500);
      }
    },
    async saveDefault() {
      this.saving = true;
      try {
        await this.$apollo.mutate({
          mutation: SetTemplateDefaultMutation,
          variables: { templateId: Number(this.currentTemplate?.id) }
        });
        pushToast("Saved as default!", "success", 500);
        await this.fetchCompanyTemplates();
        return true;
      } catch (e) {
        pushToast("Set as default failed", "error", 1500);
        return false;
      } finally { this.saving = false; }
    },
  },
});
</script>


<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  /* slate-200 */
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
  /* slate-300 */
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
  background-size: 50px 50px, 10px 10px;
  background-image:
    linear-gradient(to right, rgba(0, 0, 0, 0.07) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(0, 0, 0, 0.07) 1px, transparent 1px),
    linear-gradient(to right, rgba(0, 0, 0, 0.03) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(0, 0, 0, 0.03) 1px, transparent 1px);
}

.template-reveal-animation {
  animation: templateRevealFade 500ms ease-in-out;
}

@keyframes templateRevealFade {
  0% {
    background-color: transparent;
  }
  50% {
    background-color: rgba(20, 184, 166, 0.2); /* teal blue background */
  }
  100% {
    background-color: transparent;
  }
}
</style>
